import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { parseEmbed } from "@/lib/media";

const Slide = ({ m, title, autoPlay }) => {
  if (m.kind === "embed") {
    const e = parseEmbed(m.url);
    return <iframe src={`${e.embed}${autoPlay ? "&autoplay=1" : ""}`} title={title} className="w-full h-full" allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen data-testid="gallery-embed" />;
  }
  if (m.kind === "video") return <video src={mediaUrl(m.url)} poster={m.poster ? mediaUrl(m.poster) : undefined} controls playsInline autoPlay={autoPlay} className="w-full h-full object-contain bg-black" data-testid="gallery-video" />;
  return <img src={mediaUrl(m.url)} alt={title} className="w-full h-full object-contain bg-black" />;
};

const Thumb = ({ m }) => {
  const src = m.kind === "embed" ? parseEmbed(m.url).thumb : m.kind === "video" ? (m.poster ? mediaUrl(m.poster) : "") : mediaUrl(m.url);
  return (
    <>
      {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#1a1a1a]" />}
      {m.kind !== "image" && <span className="absolute inset-0 flex items-center justify-center bg-black/30"><Play size={16} className="text-[#D4AF37] fill-[#D4AF37]" /></span>}
    </>
  );
};

export const MediaGallery = ({ media, title = "", startIndex = 0, autoPlayStart = false }) => {
  const [idx, setIdx] = useState(startIndex);
  const [touched, setTouched] = useState(false);
  if (!media.length) return null;
  const go = (d) => { setTouched(true); setIdx((i) => (i + d + media.length) % media.length); };
  return (
    <div>
      <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black aspect-video" data-testid="gallery-viewer">
        <AnimatePresence mode="wait">
          <motion.div key={idx} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0">
            <Slide m={media[idx]} title={`${title} ${idx + 1}`} autoPlay={touched || (autoPlayStart && idx === startIndex)} />
          </motion.div>
        </AnimatePresence>
        {media.length > 1 && (
          <>
            <button onClick={() => go(-1)} data-testid="gallery-prev" className="absolute left-3 top-1/2 -translate-y-1/2 glass rounded-full p-2 text-white hover:text-[#D4AF37] z-10"><ChevronLeft size={22} /></button>
            <button onClick={() => go(1)} data-testid="gallery-next" className="absolute right-3 top-1/2 -translate-y-1/2 glass rounded-full p-2 text-white hover:text-[#D4AF37] z-10"><ChevronRight size={22} /></button>
          </>
        )}
      </div>
      {media.length > 1 && (
        <div className="flex gap-2 mt-4 overflow-x-auto no-scrollbar pb-2">
          {media.map((m, i) => (
            <button key={i} onClick={() => { setTouched(true); setIdx(i); }} data-testid={`gallery-thumb-${i}`}
              className={`relative shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 transition-colors ${i === idx ? "border-[#D4AF37]" : "border-white/10 hover:border-white/30"}`}>
              <Thumb m={m} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
